import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import Icon from '../../../components/AppIcon';

const WebhookEventsPanel = ({ compact = false, limit = 50, refreshTrigger }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    eventType: '',
    status: ''
  });

  useEffect(() => {
    loadEvents();
  }, [filters, refreshTrigger]);

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService?.getWebhookEvents({
        limit,
        eventType: filters?.eventType || null,
        status: filters?.status || null
      });
      setEvents(data);
    } catch (err) {
      console.error('Load webhook events error:', err);
      setError(err?.message || 'Failed to load webhook events');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusConfig = (status) => {
    const configs = {
      processed: { label: 'Success', color: 'text-success', bgColor: 'bg-success/10', icon: 'CheckCircle' },
      pending: { label: 'Pending', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'Clock' },
      failed: { label: 'Failed', color: 'text-destructive', bgColor: 'bg-destructive/10', icon: 'XCircle' }
    };
    return configs?.[status] || configs?.pending;
  };

  const getRetryCount = (payload) => {
    return payload?.retry_count || payload?.attempt || 0;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Webhook" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Stripe Webhook Events</h2>
            <p className="text-sm text-muted-foreground">Real-time webhook delivery tracking with retry attempts</p>
          </div>
        </div>
        {!compact && (
          <button
            onClick={loadEvents}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {!compact && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Event Type</label>
            <select
              value={filters?.eventType}
              onChange={(e) => setFilters({ ...filters, eventType: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Types</option>
              <option value="checkout.session.completed">Checkout Completed</option>
              <option value="checkout.session.expired">Checkout Expired</option>
              <option value="invoice.payment_succeeded">Payment Succeeded</option>
              <option value="invoice.payment_failed">Payment Failed</option>
              <option value="customer.subscription.created">Subscription Created</option>
              <option value="customer.subscription.updated">Subscription Updated</option>
              <option value="customer.subscription.deleted">Subscription Deleted</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={filters?.status}
              onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              <option value="processed">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {events?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No webhook events found</p>
          </div>
        ) : (
          events?.map((event) => {
            const statusConfig = getStatusConfig(event?.processingStatus);
            const retryCount = getRetryCount(event?.payload);
            const isExpanded = expandedId === event?.id;

            return (
              <div
                key={event?.id}
                className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-medium text-foreground">{event?.eventType}</span>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${statusConfig?.bgColor}`}>
                        <Icon name={statusConfig?.icon} size={12} className={statusConfig?.color} />
                        <span className={`text-xs font-medium ${statusConfig?.color}`}>{statusConfig?.label}</span>
                      </div>
                      {retryCount > 0 && (
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted">
                          <Icon name="RotateCcw" size={12} className="text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">{retryCount} retries</span>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-muted-foreground mb-2">Event ID: {event?.eventId}</p>

                    {event?.errorMessage && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded p-2 mb-2">
                        <p className="text-xs text-destructive">{event?.errorMessage}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{adminService?.formatRelativeTime(event?.createdAt)}</span>
                      <span>{adminService?.formatDate(event?.createdAt)}</span>
                      {event?.processedAt && (
                        <span>Processed: {adminService?.formatRelativeTime(event?.processedAt)}</span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => toggleExpand(event?.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
                  >
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      size={16}
                      className="text-muted-foreground"
                    />
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm font-medium text-foreground mb-2">Full Event Payload:</p>
                    <div className="bg-muted/50 rounded p-3 overflow-x-auto">
                      <pre className="text-xs font-mono text-foreground">
                        {JSON?.stringify(event?.payload, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WebhookEventsPanel;